import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  // Treat a stored Bearer token as "session likely valid" — prevents
  // mid-session redirects to /login when /auth/me transiently 429s or
  // when AuthContext is still rehydrating.
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('granullo_token');

  useEffect(() => {
    if (!loading) {
      setIsReady(true);
    }
  }, [loading]);

  if (!isReady || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Logged out AND no stored token → redirect.
  if (!user && !hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role-scoped frontend guard: external supplant_sandbox users may only
  // reach SupPlant surfaces. Backend already enforces this (auth_guard
  // middleware returns 403); this guard keeps the UX clean so the CTO never
  // lands on a shell page for a module they can't use.
  if (user?.role === 'supplant_sandbox') {
    const path = location.pathname;
    const allowed =
      path === '/' ||
      path === '/login' ||
      path === '/profile' ||
      path === '/sandbox/supplant' ||
      path.startsWith('/sandbox/supplant/');
    if (!allowed) {
      return <Navigate to="/sandbox/supplant/embedded" replace />;
    }
  }

  // Policy Lab evaluators have exactly one surface too. Same reasoning: the
  // backend already refuses everything else, this keeps the UX honest.
  if (user?.role === 'policy_lab_sandbox') {
    const path = location.pathname;
    const allowed =
      path === '/' ||
      path === '/login' ||
      path === '/profile' ||
      path === '/sandbox/policy-lab' ||
      path.startsWith('/sandbox/policy-lab/');
    if (!allowed) {
      return <Navigate to="/sandbox/policy-lab" replace />;
    }
  }

  return children;
};